import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { detectPosition } from '../utils/playerUtils';

const Dashboard = ({ matches, players, loading, isAdmin, onDeleteMatch, onNavigate }) => {
  const getTopScorers = () => {
    const forwards = players.filter(p => detectPosition(p) === 'Forward');
    return forwards.sort((a, b) => b.total_goals - a.total_goals).slice(0, 5);
  };

  const getTopGoalkeepers = () => {
    const goalkeepers = players.filter(p => detectPosition(p) === 'Goalkeeper');
    return goalkeepers.sort((a, b) => (b.clean_sheets || 0) - (a.clean_sheets || 0)).slice(0, 5);
  };

  const getTopPlaymakers = () => {
    const midfielders = players.filter(p => detectPosition(p) === 'Midfielder');
    return midfielders.sort((a, b) => b.total_assists - a.total_assists).slice(0, 5);
  };

  const getGoalDistributionByPosition = () => {
    const positions = ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'];
    return positions.map(pos => ({
      name: pos,
      goals: players.filter(p => detectPosition(p) === pos).reduce((sum, p) => sum + p.total_goals, 0)
    })).filter(item => item.goals > 0);
  };

  const getTopPlayerRadarData = () => {
    if (players.length === 0) return [];
    const topPlayer = [...players].sort((a, b) => b.total_goals - a.total_goals)[0];
    const maxGoals = Math.max(...players.map(p => p.total_goals)) || 10;
    const maxAssists = Math.max(...players.map(p => p.total_assists)) || 10;
    const maxSaves = Math.max(...players.map(p => p.total_saves)) || 10;
    
    return [
      { attribute: 'Goals', value: (topPlayer.total_goals / maxGoals) * 100, fullMark: 100 },
      { attribute: 'Assists', value: (topPlayer.total_assists / maxAssists) * 100, fullMark: 100 },
      { attribute: 'Saves', value: (topPlayer.total_saves / maxSaves) * 100, fullMark: 100 },
      { attribute: 'Matches', value: (topPlayer.matches_played / Math.max(...players.map(p => p.matches_played))) * 100, fullMark: 100 }
    ];
  };

  const getPerformanceTrend = () => {
    const recentMatches = [...matches].sort((a, b) => new Date(b.match_date) - new Date(a.match_date)).slice(0, 10).reverse();
    
    return recentMatches.map((match, index) => {
      let matchGoals = 0;
      players.forEach(player => {
        if (player.history && Array.isArray(player.history)) {
          const performance = player.history.find(h => h.match_id === match.id || h.date === match.match_date);
          if (performance && performance.goals) {
            matchGoals += performance.goals;
          }
        }
      });
      return {
        match: `M${index + 1}`,
        goals: matchGoals 
      };
    });
  };

  const safePlayers = Array.isArray(players) ? players : [];
  const safeMatches = Array.isArray(matches) ? matches : [];

  const totalGoals = safePlayers.reduce((sum, p) => sum + (p.total_goals || 0), 0);
  const totalAssists = safePlayers.reduce((sum, p) => sum + (p.total_assists || 0), 0);

  // Eclipse FC Premium Colors
  const COLORS = ['#1E7A46', '#3b82f6', '#10b981', '#a855f7'];
  
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-container-high border border-outline-variant/30 rounded-xl p-4 shadow-2xl backdrop-blur-md">
          <p className="text-on-background font-lexend font-bold mb-1">{payload[0].payload.name || payload[0].payload.match}</p>
          <p className="text-primary font-manrope font-semibold">{payload[0].value} {payload[0].name || 'Goals'}</p>
        </div>
      );
    }
    return null;
  };

  const ProgressRing = ({ progress, size = 120, strokeWidth = 8, color = '#1E7A46' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          stroke="#333333"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-24">
      {/* Premium Hero Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Matches Card */}
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
          <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <span className="material-symbols-outlined text-[120px] text-primary">stadium</span>
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 text-primary shadow-lg shadow-primary/10 border border-primary/20">
              <span className="material-symbols-outlined" style={{fontVariationSettings: "'FILL' 1"}}>sports_soccer</span>
            </div>
            <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">{safeMatches.length}</h3>
            <p className="font-manrope text-xs text-stone-600 font-bold uppercase tracking-widest mt-2">Total Matches</p>
          </div>
        </div>

        {/* Squad Card */}
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
          <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <span className="material-symbols-outlined text-[120px] text-blue-700">groups</span>
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 text-blue-700 shadow-lg shadow-blue-500/10 border border-blue-500/20">
              <span className="material-symbols-outlined" style={{fontVariationSettings: "'FILL' 1"}}>group</span>
            </div>
            <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">{safePlayers.length}</h3>
            <p className="font-manrope text-xs text-stone-600 font-bold uppercase tracking-widest mt-2">Registered Squad</p>
          </div>
        </div>

        {/* Goals Card */}
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
          <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <span className="material-symbols-outlined text-[120px] text-rose-700">sports_score</span>
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-rose-500/10 rounded-2xl flex items-center justify-center mb-6 text-rose-700 shadow-lg shadow-rose-500/10 border border-rose-500/20">
              <span className="material-symbols-outlined" style={{fontVariationSettings: "'FILL' 1"}}>sports_score</span>
            </div>
            <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">{totalGoals}</h3>
            <p className="font-manrope text-xs text-stone-600 font-bold uppercase tracking-widest mt-2">Total Goals</p>
          </div>
        </div>

        {/* Assists Card */}
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 relative overflow-hidden group hover:-translate-y-1 hover:bg-surface-container-high transition-all duration-300 shadow-lg">
          <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <span className="material-symbols-outlined text-[120px] text-emerald-700">handshake</span>
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6 text-emerald-700 shadow-lg shadow-emerald-500/10 border border-emerald-500/20">
              <span className="material-symbols-outlined" style={{fontVariationSettings: "'FILL' 1"}}>handshake</span>
            </div>
            <h3 className="text-4xl sm:text-5xl font-lexend font-black text-on-background tracking-tight">{totalAssists}</h3>
            <p className="font-manrope text-xs text-stone-600 font-bold uppercase tracking-widest mt-2">Total Assists</p>
          </div>
        </div>
      </div>

      {safePlayers.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          
          {/* Goal Distribution Donut Chart */}
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div className="mb-8">
              <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Goal Distribution</h2>
              <p className="text-stone-600 text-sm font-manrope">Positional scoring breakdown</p>
            </div>
            
            <div className="flex flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={getGoalDistributionByPosition()}
                    cx="50%"
                    cy="50%"
                    innerRadius={80}
                    outerRadius={110}
                    paddingAngle={8}
                    dataKey="goals"
                    stroke="none"
                    cornerRadius={8}
                  >
                    {getGoalDistributionByPosition().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-6 w-full max-w-sm">
                {getGoalDistributionByPosition().map((item, index) => (
                  <div key={item.name} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full shadow-lg" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span className="text-stone-600 text-sm font-manrope">{item.name}</span>
                    <span className="text-on-background font-bold font-lexend ml-auto">{item.goals}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top Player Radar Chart */}
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
            <div className="mb-8 flex justify-between items-start">
              <div>
                <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Apex Performer</h2>
                <p className="text-stone-600 text-sm font-manrope">
                  <span className="text-primary font-bold">{[...players].sort((a, b) => b.total_goals - a.total_goals)[0]?.name}</span>'s Stat Profile
                </p>
              </div>
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center border border-primary/20">
                <span className="material-symbols-outlined text-primary text-[20px]">military_tech</span>
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={getTopPlayerRadarData()} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#333333" />
                <PolarAngleAxis dataKey="attribute" tick={{ fill: '#a8a29e', fontSize: 13, fontFamily: 'Manrope', fontWeight: 600 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                <Radar name="Performance" dataKey="value" stroke="#1E7A46" strokeWidth={3} fill="#1E7A46" fillOpacity={0.4} />
                <Tooltip content={<CustomTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Performance Trend Line Chart */}
      {safePlayers.length > 0 && (
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
          <div className="mb-8">
            <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Form Timeline</h2>
            <p className="text-stone-600 text-sm font-manrope">Goal volume across recent fixtures</p>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={getPerformanceTrend()} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorGoals" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E7A46" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#1E7A46" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis dataKey="match" stroke="#78716c" fontSize={12} fontFamily="Manrope" tickMargin={10} axisLine={false} tickLine={false} />
              <YAxis stroke="#78716c" fontSize={12} fontFamily="Manrope" axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="goals"
                stroke="#1E7A46"
                strokeWidth={4}
                fill="url(#colorGoals)"
                dot={{ fill: '#0b1326', r: 6, strokeWidth: 3, stroke: '#1E7A46' }}
                activeDot={{ r: 8, strokeWidth: 0, fill: '#1E7A46' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Top Scorers - Gradient Bar Chart */}
        {getTopScorers().length > 0 && (
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Golden Boot</h2>
                <p className="text-stone-600 text-sm font-manrope">Leading goal scorers</p>
              </div>
              <button 
                onClick={() => onNavigate('forward')}
                className="px-4 py-2 bg-background border border-outline-variant/30 text-stone-600 rounded-xl hover:bg-surface-container-high hover:text-white transition-all text-xs font-bold font-lexend uppercase tracking-widest active:scale-95"
              >
                View Full
              </button>
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={getTopScorers()} margin={{ top: 20, right: 0, left: -20, bottom: 20 }}>
                <defs>
                  <linearGradient id="goalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E7A46" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#a8a29e', fontFamily: 'Manrope' }}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={10}
                />
                <YAxis tick={{ fontSize: 12, fill: '#a8a29e', fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{fill: '#1a1a1a'}} />
                <Bar 
                  dataKey="total_goals" 
                  fill="url(#goalGradient)" 
                  name="Goals" 
                  radius={[8, 8, 0, 0]}
                  barSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Playmakers */}
        {getTopPlaymakers().length > 0 && (
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Playmakers</h2>
                <p className="text-stone-600 text-sm font-manrope">Most creative impact</p>
              </div>
              <button 
                onClick={() => onNavigate('midfielder')}
                className="px-4 py-2 bg-background border border-outline-variant/30 text-stone-600 rounded-xl hover:bg-surface-container-high hover:text-white transition-all text-xs font-bold font-lexend uppercase tracking-widest active:scale-95"
              >
                View Full
              </button>
            </div>

            <div className="space-y-4 flex-1">
              {getTopPlaymakers().map((player, index) => {
                const maxAssists = getTopPlaymakers()[0].total_assists || 1;
                const percentage = (player.total_assists / maxAssists) * 100;
                
                return (
                  <div key={player.id} className="flex items-center gap-4 p-4 bg-background/50 rounded-2xl hover:bg-surface-container-high transition-all cursor-pointer border border-outline-variant/10 group">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 bg-surface-container-highest rounded-xl flex items-center justify-center border border-outline-variant/30">
                        <span className="text-on-background font-bold font-lexend text-lg">{player.name.charAt(0)}</span>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-primary rounded-lg flex items-center justify-center border-2 border-background">
                        <span className="text-on-primary text-[10px] font-black">{index + 1}</span>
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-on-background font-lexend font-bold truncate text-lg">{player.name}</p>
                      <div className="mt-2 h-1.5 bg-surface-container-high rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="text-right pl-4 border-l border-outline-variant/20">
                      <p className="text-primary text-2xl font-black font-lexend leading-none">{player.total_assists}</p>
                      <p className="text-stone-700 text-[10px] font-bold uppercase tracking-widest mt-1 font-manrope">AST</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Elite Goalkeepers */}
      {getTopGoalkeepers().length > 0 && (
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">The Wall</h2>
              <p className="text-stone-600 text-sm font-manrope">Clean sheet leaders</p>
            </div>
            <button 
              onClick={() => onNavigate('goalkeeper')}
              className="px-4 py-2 bg-background border border-outline-variant/30 text-stone-600 rounded-xl hover:bg-surface-container-high hover:text-white transition-all text-xs font-bold font-lexend uppercase tracking-widest active:scale-95"
            >
              View Full
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {getTopGoalkeepers().map((gk) => {
              const cleanSheetPercentage = (gk.clean_sheets / (gk.matches_played || 1)) * 100;
              
              return (
                <div key={gk.id} className="bg-background/50 rounded-2xl p-5 text-center hover:bg-surface-container-high transition-all border border-outline-variant/10 group cursor-pointer">
                  <div className="relative inline-block mb-4">
                    <ProgressRing progress={cleanSheetPercentage} size={100} strokeWidth={6} color="#a855f7" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-on-background text-3xl font-lexend font-black">{gk.clean_sheets || 0}</p>
                        <p className="text-stone-700 text-[9px] font-bold uppercase tracking-widest font-manrope">Clean</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-on-background font-bold font-lexend truncate">{gk.name}</p>
                  <div className="mt-4 pt-4 border-t border-outline-variant/20 grid grid-cols-2 gap-2 text-center">
                    <div>
                      <p className="text-stone-700 text-[10px] font-bold uppercase tracking-widest mb-1">Saves</p>
                      <p className="text-blue-700 font-bold font-manrope">{gk.total_saves || 0}</p>
                    </div>
                    <div>
                      <p className="text-stone-700 text-[10px] font-bold uppercase tracking-widest mb-1">Rate</p>
                      <p className="text-emerald-700 font-bold font-manrope">
                        {gk.total_shots_faced > 0 ? Math.round((gk.total_saves / gk.total_shots_faced) * 100) : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Data State - FIXED TO SHOW MESSAGE INSTEAD OF 0 */}
      {safeMatches.length === 0 && (
        <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-16 text-center shadow-lg">
          <div className="w-20 h-20 bg-background rounded-full flex items-center justify-center mx-auto mb-6 border border-outline-variant/30 shadow-inner">
            <span className="material-symbols-outlined text-4xl text-stone-800">sports_score</span>
          </div>
          <h3 className="text-3xl sm:text-4xl font-black text-on-background font-lexend mb-3">
            No matches logged yet
          </h3>
          <p className="text-stone-600 font-manrope mb-8 max-w-md mx-auto text-lg">
            Your stadium is quiet. Head to the Admin Suite to log your first match and bring the dashboard to life.
          </p>
          {isAdmin && (
            <button
              onClick={() => onNavigate('addMatch')}
              className="bg-primary hover:bg-primary/90 text-on-primary px-8 py-4 rounded-xl font-bold font-lexend uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-primary/20 flex items-center justify-center gap-3 mx-auto"
            >
              <span className="material-symbols-outlined">add_circle</span>
              Initialize Match Log
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
